import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Paintbrush, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import ImageUploader from "@/components/ImageUploader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PRESETS = [
  { type: "color", value: "#0b1220", label: "Noite" },
  { type: "color", value: "#e7f5ee", label: "Menta" },
  { type: "color", value: "#fdf2e9", label: "Pêssego" },
  { type: "color", value: "#eef2ff", label: "Lavanda" },
  { type: "color", value: "#fff7d6", label: "Areia" },
  { type: "color", value: "#e0f2fe", label: "Céu" },
  { type: "gradient", value: "linear-gradient(160deg,#0f766e 0%,#0ea5e9 100%)", label: "Futuro" },
  { type: "gradient", value: "linear-gradient(160deg,#111827 0%,#312e81 100%)", label: "Galáxia" },
  { type: "gradient", value: "linear-gradient(160deg,#f97316 0%,#db2777 50%,#7c3aed 100%)", label: "Pôr do sol" },
  { type: "gradient", value: "linear-gradient(160deg,#fde68a 0%,#fca5a5 100%)", label: "Verão" },
  { type: "gradient", value: "radial-gradient(circle at 20% 10%,#a7f3d0 0%,#e0f2fe 45%,#fdf4ff 100%)", label: "Aurora" },
  { type: "gradient", value: "repeating-linear-gradient(45deg,#f1f5f9 0 12px,#e2e8f0 12px 24px)", label: "Listras" },
];

/** CSS style for a wallpaper object (null → default theme background). */
export function wallpaperStyle(wp) {
  if (!wp) return {};
  if (wp.type === "color") return { background: wp.value };
  if (wp.type === "gradient") return { background: wp.value };
  if (wp.type === "image") {
    const dim = (wp.dim ?? 30) / 100;
    return {
      backgroundImage: `linear-gradient(rgba(0,0,0,${dim}), rgba(0,0,0,${dim})), url("${wp.value}")`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    };
  }
  return {};
}

function Swatch({ wp, selected, onClick, testid, label }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick} title={label}
      className={`relative h-16 rounded-xl border-2 overflow-hidden transition-transform hover:scale-[1.03] ${selected ? "border-primary ring-2 ring-primary/40" : "border-border"}`}
      style={wallpaperStyle(wp)}>
      {selected && <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center"><Check className="w-3 h-3" /></span>}
      <span className="absolute bottom-1 left-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-black/50 text-white">{label}</span>
    </button>
  );
}

export default function ChatWallpaperDialog({ open, onOpenChange, conversationId, conversationName, current, onSaved }) {
  const [draft, setDraft] = useState(current || null);
  const [scope, setScope] = useState("conversation"); // conversation | all
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setDraft(current || null); setScope(conversationId ? "conversation" : "all"); } }, [open, current, conversationId]);

  const same = (a, b) => a && b && a.type === b.type && a.value === b.value;

  const save = async (wallpaper) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/chat/wallpaper`, { conversation_id: scope === "all" ? null : conversationId, wallpaper });
      onSaved?.(r.data);
      toast.success(wallpaper ? (scope === "all" ? "Papel de parede aplicado em todas as conversas" : "Papel de parede desta conversa atualizado") : "Papel de parede restaurado");
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="chat-wallpaper-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Paintbrush className="w-5 h-5 text-primary" /> Papel de parede do chat</DialogTitle></DialogHeader>

        <div className="rounded-xl border overflow-hidden h-36 relative" style={wallpaperStyle(draft)} data-testid="chat-wallpaper-preview">
          {!draft && <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--muted))_0%,transparent_60%)]" />}
          <div className="absolute left-3 top-4 max-w-[60%] rounded-2xl rounded-bl-md bg-card border border-cyan-500/15 px-3 py-1.5 text-xs shadow-sm">Oi! Como fica o fundo?</div>
          <div className="absolute right-3 bottom-4 max-w-[60%] rounded-2xl rounded-br-md bg-gradient-to-br from-teal-500 via-cyan-500 to-sky-600 text-white px-3 py-1.5 text-xs shadow-sm">Ficou ótimo 🎨</div>
        </div>

        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="chat-wallpaper-presets">
          {PRESETS.map((p) => (
            <Swatch key={p.value} wp={p} label={p.label} selected={same(draft, p)} onClick={() => setDraft({ ...p })} testid={`wallpaper-preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`} />
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Cor personalizada</span>
            <input type="color" data-testid="wallpaper-color-input" value={draft?.type === "color" && /^#[0-9a-fA-F]{6}$/.test(draft.value) ? draft.value : "#e7f5ee"}
              onChange={(e) => setDraft({ type: "color", value: e.target.value })} className="w-9 h-9 rounded-md border cursor-pointer bg-transparent" />
          </label>
          <ImageUploader testid="wallpaper-image-upload" label="Enviar imagem" variant="outline" size="sm" accept="image/*" onUploaded={(url) => setDraft({ type: "image", value: url, dim: 30 })} />
        </div>

        {draft?.type === "image" && (
          <label className="text-xs text-muted-foreground flex items-center gap-3" data-testid="wallpaper-dim-row">
            Escurecer imagem
            <input type="range" min={0} max={80} value={draft.dim ?? 30} data-testid="wallpaper-dim-slider" onChange={(e) => setDraft({ ...draft, dim: Number(e.target.value) })} className="flex-1 accent-primary" />
            <span className="w-8 text-right">{draft.dim ?? 30}%</span>
          </label>
        )}

        {conversationId && (
          <div className="flex gap-2 text-xs" data-testid="wallpaper-scope">
            <button type="button" data-testid="wallpaper-scope-conversation" onClick={() => setScope("conversation")}
              className={`flex-1 rounded-full border px-3 py-1.5 font-medium ${scope === "conversation" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>Só com {conversationName?.split(" ")[0] || "esta conversa"}</button>
            <button type="button" data-testid="wallpaper-scope-all" onClick={() => setScope("all")}
              className={`flex-1 rounded-full border px-3 py-1.5 font-medium ${scope === "all" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>Todas as conversas</button>
          </div>
        )}

        <div className="flex gap-2 justify-between">
          <Button variant="ghost" className="rounded-full gap-1" data-testid="wallpaper-reset" onClick={() => save(null)} disabled={saving}><RotateCcw className="w-4 h-4" /> Padrão</Button>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
            <Button className="rounded-full" data-testid="wallpaper-save" onClick={() => save(draft)} disabled={saving || !draft}>{saving ? "Salvando..." : "Aplicar"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
